import React from 'react'
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import { PRM_CombinedResponse } from '../types/unified_models'

interface StdoutDisplayProps {
  response: PRM_CombinedResponse | null
}

export default function StdoutDisplay({ response }: StdoutDisplayProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const codeBg = useColorModeValue('gray.100', 'gray.800')

  if (!response) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={InfoIcon} color="gray.400" />
          <Heading size="sm" color={textColor}>Program Output</Heading>
        </HStack>
        <Text fontSize="sm" color={textColor}>No output available. Run your code to see the output.</Text>
      </VStack>
    )
  }

  // Build the output string based on the response type
  let outputContent = ''
  let hasOutput = false

  // Check if it's a custom input run
  if (response.custom) {
    if (response.custom.stdout) {
      outputContent += response.custom.stdout
      hasOutput = true
    }
    if (response.custom.stderr) {
      outputContent += response.custom.stderr
      hasOutput = true
    }
  } else if (response.benchmark) {
    // Check if benchmark has an error
    if ('error' in response.benchmark && response.benchmark.error) {
      // Error case: combine all available output
      if (response.correctness?.stderr) {
        outputContent += response.correctness.stderr
        hasOutput = true
      }
      if (response.correctness?.stdout) {
        outputContent += response.correctness.stdout
        hasOutput = true
      }
      if (response.benchmark.stdout) {
        outputContent += response.benchmark.stdout
        hasOutput = true
      }
      if (response.benchmark.stderr) {
        outputContent += response.benchmark.stderr
        hasOutput = true
      }
    } else {
      // Success case: combine correctness and benchmark test output
      if (response.correctness?.stderr) {
        outputContent += response.correctness.stderr
        hasOutput = true
      }
      if (response.correctness?.stdout) {
        outputContent += response.correctness.stdout
        hasOutput = true
      }
      if ('tests' in response.benchmark && response.benchmark.tests?.stdout) {
        outputContent += response.benchmark.tests.stdout
        hasOutput = true
      }
    }
  }

  if (!hasOutput) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={InfoIcon} color="gray.400" />
          <Heading size="sm" color={textColor}>Program Output</Heading>
        </HStack>
        <Text fontSize="sm" color={textColor}>No output generated from the program execution.</Text>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={3}>
      <HStack>
        <Icon as={InfoIcon} color="blue.400" />
        <Heading size="sm" color={textColor}>Program Output</Heading>
      </HStack>
      <Box
        as="pre"
        bg={codeBg}
        p={3}
        borderRadius="md"
        fontSize="xs"
        fontFamily="mono"
        overflowX="auto"
        maxH="200px"
        overflowY="auto"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        border="1px"
        borderColor="gray.200"
      >
        {outputContent || 'No output available'}
      </Box>
    </VStack>
  )
} 